import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, User } from 'lucide-react'

import type { ProductionAssignment, ProductionMember } from '@/api/studio-production'
import { memberDisplayName } from '@/features/studio/modals/InviteMemberModal'
import {
  PRODUCTION_TABLE_HEAD_ALT_CLASS,
  PRODUCTION_TABLE_ROW_CLASS,
  PRODUCTION_TABLE_ROW_HOVER_CLASS,
} from '@/features/studio/lib/production-shell'
import { cn } from '@/shared/lib/utils'
import { ProductionEmptyPanel, ProtoBadge, ProtoInput, ProtoListTable } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

const STATUS_TONE: Record<string, 'amber' | 'emerald' | 'indigo' | 'rose' | 'default'> = {
  pending: 'amber',
  in_progress: 'indigo',
  submitted: 'rose',
  approved: 'emerald',
  changes_requested: 'amber',
}

type AssigneeGroup = {
  assigneeId: number
  departmentName: string
  assignments: ProductionAssignment[]
}

function groupByAssignee(assignments: ProductionAssignment[]): AssigneeGroup[] {
  const map = new Map<number, AssigneeGroup>()
  const order: number[] = []
  for (const a of assignments) {
    let g = map.get(a.assignee_id)
    if (!g) {
      g = { assigneeId: a.assignee_id, departmentName: a.department_name ?? '', assignments: [] }
      map.set(a.assignee_id, g)
      order.push(a.assignee_id)
    }
    if (!g.departmentName && a.department_name) {
      g.departmentName = a.department_name
    }
    g.assignments.push(a)
  }
  return order.map((id) => map.get(id)!)
}

export function ProductionAssignmentsTable({
  assignments,
  members,
  emptyMessage,
  resolveScopeLabel,
  showWorkflowColumns = false,
  reviewNotes,
  onReviewNoteChange,
  renderActions,
}: {
  assignments: ProductionAssignment[]
  members: ProductionMember[]
  emptyMessage: string
  resolveScopeLabel: (
    itemType: string,
    itemPublicId: string,
  ) => { kindLabel: string; title: string }
  showWorkflowColumns?: boolean
  reviewNotes?: Record<string, string>
  onReviewNoteChange?: (publicId: string, note: string) => void
  renderActions?: (assignment: ProductionAssignment) => ReactNode
}) {
  const { t } = useTranslation()
  const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set())

  const assigneeGroups = useMemo(() => groupByAssignee(assignments), [assignments])
  const memberNameById = useMemo(
    () => new Map(members.map((m) => [m.user_id, memberDisplayName(m)])),
    [members],
  )

  useEffect(() => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      for (const g of assigneeGroups) {
        next.add(g.assigneeId)
      }
      return next
    })
  }, [assigneeGroups])

  function toggleExpanded(assigneeId: number) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(assigneeId)) next.delete(assigneeId)
      else next.add(assigneeId)
      return next
    })
  }

  function statusLabel(a: ProductionAssignment) {
    return a.status === 'changes_requested'
      ? t('tasks.changesRequested')
      : t(`tasks.assignmentColumn.${a.status}`, { defaultValue: a.status })
  }

  function renderScope(a: ProductionAssignment) {
    if ((a.items ?? []).length === 0) {
      return <span className="text-xs text-zinc-600">{t('team.assignmentsNoScope')}</span>
    }
    return (
      <ul className="space-y-1">
        {(a.items ?? []).map((it, idx) => {
          const item = resolveScopeLabel(it.item_type, it.item_public_id)
          return (
            <li key={`${it.item_type}:${it.item_public_id}:${idx}`} className="text-xs">
              <span className="text-zinc-500">{item.kindLabel}</span>
              <span className="mx-1 text-zinc-600">·</span>
              <span className="font-medium text-zinc-200">{item.title}</span>
            </li>
          )
        })}
      </ul>
    )
  }

  if (assigneeGroups.length === 0) {
    return <ProductionEmptyPanel>{emptyMessage}</ProductionEmptyPanel>
  }

  return (
    <ProtoListTable production>
      <table className="min-w-full text-left text-sm">
        <thead className={cn(PRODUCTION_TABLE_HEAD_ALT_CLASS, 'text-xs text-zinc-500')}>
          <tr>
            <th className="px-4 py-3">{t('team.assignmentsColAssignee')}</th>
            <th className="px-4 py-3">{t('team.assignmentsColDept')}</th>
            <th className="px-4 py-3">{t('team.assignmentsColScope')}</th>
            <th className="px-4 py-3">{t('team.assignmentsColQuota')}</th>
            <th className="px-4 py-3">{t('team.assignmentsColStatus')}</th>
            {showWorkflowColumns ? (
              <>
                <th className="px-4 py-3">{t('tasks.colReviewNote')}</th>
                <th className="px-4 py-3 text-right">{t('team.colActions')}</th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {assigneeGroups.map((group) => {
            const expanded = expandedKeys.has(group.assigneeId)
            const name = memberNameById.get(group.assigneeId) ?? `#${group.assigneeId}`
            const totalRemaining = group.assignments.reduce((s, a) => s + a.quota_remaining, 0)
            const totalAllocated = group.assignments.reduce((s, a) => s + a.quota_allocated, 0)
            return (
              <Fragment key={group.assigneeId}>
                <tr className={cn(PRODUCTION_TABLE_ROW_CLASS, 'bg-white/[0.02]')}>
                  <td className="px-4 py-2.5" colSpan={2}>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-start gap-2 text-left text-sm font-medium text-zinc-200"
                      onClick={() => toggleExpanded(group.assigneeId)}
                    >
                      {expanded ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-zinc-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-zinc-500" />
                      )}
                      <User className="h-4 w-4 shrink-0 text-sky-400" />
                      <span>{name}</span>
                      {group.departmentName ? (
                        <span className="text-xs font-normal text-zinc-500">· {group.departmentName}</span>
                      ) : null}
                      <span className="text-xs font-normal text-zinc-600">
                        {t('team.assignmentsTaskCount', { count: group.assignments.length })}
                      </span>
                    </Button>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-600">—</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-amber-200/90">
                    {t('tasks.quotaRemaining', {
                      remaining: totalRemaining,
                      total: totalAllocated,
                    })}
                  </td>
                  <td className="px-4 py-2.5">—</td>
                  {showWorkflowColumns ? (
                    <>
                      <td className="px-4 py-2.5">—</td>
                      <td className="px-4 py-2.5">—</td>
                    </>
                  ) : null}
                </tr>
                {expanded
                  ? group.assignments.map((a) => (
                      <tr
                        key={a.public_id}
                        className={cn(PRODUCTION_TABLE_ROW_HOVER_CLASS, 'align-top')}
                      >
                        <td className="px-4 py-3 pl-12" colSpan={2} />
                        <td className="px-4 py-3">{renderScope(a)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-amber-200/80">
                          {t('tasks.quotaRemaining', {
                            remaining: a.quota_remaining,
                            total: a.quota_allocated,
                          })}
                        </td>
                        <td className="px-4 py-3">
                          <ProtoBadge tone={STATUS_TONE[a.status] ?? 'default'}>
                            {statusLabel(a)}
                          </ProtoBadge>
                        </td>
                        {showWorkflowColumns ? (
                          <>
                            <td className="px-4 py-3">
                              {a.status === 'submitted' && onReviewNoteChange ? (
                                <ProtoInput
                                  inputSize="compact"
                                  className="max-w-xs"
                                  placeholder={t('tasks.reviewNotePlaceholder')}
                                  value={reviewNotes?.[a.public_id] ?? ''}
                                  onChange={(e) => onReviewNoteChange(a.public_id, e.target.value)}
                                />
                              ) : a.review_note ? (
                                <span className="text-xs text-zinc-500">{a.review_note}</span>
                              ) : (
                                <span className="text-xs text-zinc-600">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3">{renderActions?.(a) ?? null}</td>
                          </>
                        ) : null}
                      </tr>
                    ))
                  : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </ProtoListTable>
  )
}
